import { Component, OnInit } from '@angular/core';

@Component({
  selector: 'app-epps-smart-erp',
  templateUrl: './epps-smart-erp.component.html',
  styleUrls: ['./epps-smart-erp.component.css']
})
export class EppsSmartErpComponent implements OnInit {

  // Banner content
  bannerContent = {
    title: 'EPPS Smart ERP — Your Running Partner in Digital Transformation',
    subtitle: 'Where Data Turns Into Decisions',
    description: 'Unify Finance, Supply Chain, Production, and People on one intelligent platform.',
    ctaPrimary: 'Explore Smart ERP',
    ctaSecondary: 'Download Playbook'
  };

  // Problem cards
  problems = [
    {
      icon: 'alert-triangle',
      title: 'Siloed Data',
      description: 'Critical business data trapped in disconnected systems, slowing decision-making and creating blind spots.'
    },
    {
      icon: 'clock',
      title: 'Manual Work',
      description: 'Time-consuming manual processes that drain productivity and increase error rates across departments.'
    },
    {
      icon: 'trending-down',
      title: 'Pricing Pressure',
      description: 'Difficulty protecting margins and pricing due to lack of real-time cost visibility and market insights.'
    },
    {
      icon: 'package',
      title: 'Supply Chain Risk',
      description: 'Unpredictable sourcing challenges and inventory imbalances that disrupt operations and customer delivery.'
    },
    {
      icon: 'users',
      title: 'Decision Lag',
      description: 'Slow decision-making processes that prevent quick responses to market changes and opportunities.'
    },
    {
      icon: 'shield-alert',
      title: 'Compliance Gaps',
      description: 'Manual compliance processes that create audit risks and regulatory exposure across business functions.'
    }
  ];

  // What changes section
  changes = [
    {
      icon: 'dollar-sign',
      title: 'Margin Protection, Not Just Monitoring',
      description: 'Cost-to-serve clarity and variance alerts at the point of decision'
    },
    {
      icon: 'package',
      title: 'Resilient Sourcing & Inventory',
      description: 'Vendor scoring, demand signals, and shortage/excess prevention'
    },
    {
      icon: 'zap',
      title: 'Faster Quote-to-Cash',
      description: 'Integrated quoting → order → fulfillment → invoicing → collections with SLA controls'
    },
    {
      icon: 'brain',
      title: 'Decisions at the Speed of Business',
      description: 'AI forecasts and live operational analytics embedded where work happens'
    },
    {
      icon: 'shield-check',
      title: 'Compliance by Design',
      description: 'Policy-driven approvals, audit trails, and data lineage across functions'
    },
    {
      icon: 'users',
      title: 'People-Centric Productivity',
      description: 'Role workbenches, mobile approvals, and automation of repetitive tasks'
    }
  ];

  // ERP Modules
  erpModules = [
    { name: 'Finance & Accounting', icon: 'calculator' },
    { name: 'Sales & Distribution', icon: 'trending-up' },
    { name: 'HRMS', icon: 'users' },
    { name: 'CRM', icon: 'user-check' },
    { name: 'Material & Inventory', icon: 'package' },
    { name: 'Production & Quality (Industry 4.0)', icon: 'settings' },
    { name: 'Subcontracting/Outsourcing', icon: 'handshake' },
    { name: 'Jobwork/Insourcing', icon: 'briefcase' },
    { name: 'Equipment Warranty/AMC', icon: 'shield' },
    { name: 'Advanced Planning', icon: 'calendar' },
    { name: 'Mobile ERP', icon: 'smartphone' },
    { name: 'Administration & Business Policy', icon: 'file-text' }
  ];

  // Connectivity features
  connectivityFeatures = [
    {
      icon: 'link',
      title: 'SAP Integration',
      description: 'Seamless integration for finance, procurement, logistics and reporting',
      extendedDescription: 'Connect EPPS Smart ERP with SAP ERP systems for unified financial data, real-time procurement workflows, and comprehensive logistics tracking. Bi-directional synchronization ensures consistency across both platforms, enabling seamless data flow for reporting, analytics, and decision-making.',
      category: 'Enterprise Systems'
    },
    {
      icon: 'layers',
      title: 'Business Systems',
      description: 'CRM, Payroll, DMS, LMS and line-of-business systems',
      extendedDescription: 'Integrate with your existing ecosystem—from Customer Relationship Management (CRM) platforms to Payroll systems, Document Management (DMS), Learning Management Systems (LMS), and specialized line-of-business applications. EPPS Smart ERP serves as the central hub, orchestrating data flow and business processes across all systems.',
      category: 'Business Applications'
    },
    {
      icon: 'credit-card',
      title: 'Payment Gateways',
      description: 'Banking & Payment Gateways, vendor/customer portals, e-commerce',
      extendedDescription: 'Enable seamless financial transactions through integrated banking gateways and payment processors. Connect with vendor portals for automated procurement workflows and customer portals for self-service capabilities. Full e-commerce integration supports online orders, inventory sync, and automated fulfillment.',
      category: 'Financial Integration'
    },
    {
      icon: 'cpu',
      title: 'IoT & Machines',
      description: 'POS, scales, sensors/gateways for shop-floor signals',
      extendedDescription: 'Bridge the physical and digital worlds with IoT integration. Connect Point of Sale (POS) systems, industrial scales, sensors, and shop-floor equipment directly to EPPS Smart ERP. Real-time data capture from machines enables predictive maintenance, automated inventory updates, and production monitoring.',
      category: 'Industrial IoT'
    },
    {
      icon: 'smartphone',
      title: 'Mobility',
      description: 'Android/iOS apps and WhatsApp/Telegram workflows',
      extendedDescription: 'Empower your workforce with mobile-first access through native Android and iOS applications. Extend workflows to messaging platforms like WhatsApp and Telegram for approvals, notifications, and quick actions. Access critical business functions anytime, anywhere, with full offline capability and real-time sync.',
      category: 'Mobile & Messaging'
    },
    {
      icon: 'cloud',
      title: 'Cloud & API Ecosystem',
      description: 'RESTful APIs, webhooks, and cloud services integration',
      extendedDescription: 'Built on open standards with comprehensive RESTful APIs and webhook support. Easily integrate with cloud services like AWS, Azure, Google Cloud, and third-party SaaS applications. Extend functionality through custom connectors or leverage our pre-built integration library for popular services.',
      category: 'Developer Platform'
    }
  ];

  // Architecture features
  architectureFeatures = [
    {
      icon: 'code',
      title: 'Open-Source Core',
      description: 'Java + PostgreSQL → scalable, no lock-in',
      extendedDescription: 'Built on enterprise-grade open-source technologies, EPPS Smart ERP leverages Java for robust backend processing and PostgreSQL for reliable, high-performance database operations. This foundation ensures scalability from small businesses to large enterprises, zero vendor lock-in, and complete control over your data and infrastructure.',
      benefits: [
        'No licensing costs for core platform',
        'Full source code access and customization',
        'Proven stability at enterprise scale',
        'Active community support and continuous improvements'
      ]
    },
    {
      icon: 'brain',
      title: 'GenAI & ML',
      description: 'Forecasting, anomaly detection, automation',
      extendedDescription: 'Intelligent automation powered by Generative AI and Machine Learning transforms your ERP from a transactional system to a predictive business partner. Advanced algorithms analyze patterns, forecast demand, detect anomalies, and automate routine decisions—freeing your team to focus on strategic initiatives.',
      benefits: [
        'AI-powered demand forecasting and inventory optimization',
        'Anomaly detection for fraud prevention and quality control',
        'Intelligent document processing and data extraction',
        'Predictive maintenance for equipment and assets'
      ]
    },
    {
      icon: 'cloud',
      title: 'Flexible Deployment',
      description: 'Cloud or on-prem; modular rollout; lower TCO',
      extendedDescription: 'Choose the deployment model that fits your security, compliance, and operational requirements. Whether cloud-hosted for rapid scalability, on-premises for maximum control, or hybrid for the best of both worlds, EPPS Smart ERP adapts to your infrastructure. Modular architecture enables phased implementation, reducing risk and accelerating time-to-value.',
      benefits: [
        'Cloud, on-premises, or hybrid deployment options',
        'Phased module-by-module implementation',
        'Lower total cost of ownership (TCO)',
        'Scalable from startup to enterprise without re-architecture'
      ]
    }
  ];

  // Why choose EPPS
  whyChooseEpps = [
    {
      icon: 'target',
      title: 'Problem-First Delivery',
      description: 'Protects pricing and de-risks supply from day one'
    },
    {
      icon: 'link',
      title: 'Open & Extensible',
      description: 'Integrates SAP and your ecosystem cleanly'
    },
    {
      icon: 'shield',
      title: 'Secure by Design',
      description: 'Defense-grade controls and auditability'
    },
    {
      icon: 'layers',
      title: 'Modular Rollout',
      description: 'Realize value fast and scale at your pace'
    }
  ];

  // Industries
  industries = [
    { name: 'Defense & Shipyards', icon: 'shield' },
    { name: 'Manufacturing', icon: 'settings' },
    { name: 'Infrastructure', icon: 'building' },
    { name: 'Enterprises', icon: 'briefcase' }
  ];

  constructor() { }

  ngOnInit(): void {
    // Initialize animations
    this.initializeAnimations();
    // Initialize Lucide icons
    this.initializeLucideIcons();
  }

  private initializeAnimations(): void {
    // First, add initial animation class to all elements
    setTimeout(() => {
      document.querySelectorAll('[data-animate]').forEach(el => {
        el.classList.add('animate-initial');
      });
    }, 50);

    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.remove('animate-initial');
          entry.target.classList.add('in-view');
        }
      });
    }, observerOptions);

    // Observe all elements with data-animate attribute
    setTimeout(() => {
      document.querySelectorAll('[data-animate]').forEach(el => {
        observer.observe(el);
      });
    }, 100);
  }

  private initializeLucideIcons(): void {
    // Initialize Lucide icons after view init
    setTimeout(() => {
      if (typeof (window as any).lucide !== 'undefined') {
        (window as any).lucide.createIcons();
      }
    }, 200);
  }

}
